import { Link, Route, Routes, useLocation } from "react-router-dom";
import { LandingPage } from "./routes/LandingPage";
import { LoginPage } from "./routes/LoginPage";
import { SignupPage } from "./routes/SignupPage";
import { DashboardPage } from "./routes/DashboardPage";
import { ProjectDetailPage } from "./routes/ProjectDetailPage";
import { StyleguidePage } from "./routes/StyleguidePage";
import { ElementGuidePage } from "./routes/ElementGuidePage";
import { SharePage } from "./routes/SharePage";
import { UsersAdminPage } from "./routes/UsersAdminPage";
import { ClientLoginPage } from "./routes/ClientLoginPage";
import { ClientDashboardPage } from "./routes/ClientDashboardPage";
import { ClientProjectPage } from "./routes/ClientProjectPage";
import { RequireAuth } from "./components/RequireAuth";
import { RequireClientAuth } from "./components/RequireClientAuth";
import { useAuth } from "./lib/auth";
import { useClientAuth } from "./lib/clientAuth";

export function App() {
  const location = useLocation();
  // The reviewer surface gets its own minimal header rendered inside the
  // SharePage itself. Outside of it, the app shell wraps content with the
  // owner/client-aware header.
  const isReviewerSurface = location.pathname.startsWith("/p/");

  return (
    <div className="min-h-screen flex flex-col">
      {isReviewerSurface ? null : <Header />}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <DashboardPage />
              </RequireAuth>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <RequireAuth>
                <ProjectDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/projects/:id/styleguide"
            element={
              <RequireAuth>
                <StyleguidePage />
              </RequireAuth>
            }
          />
          <Route
            path="/projects/:id/elements"
            element={
              <RequireAuth>
                <ElementGuidePage />
              </RequireAuth>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAuth>
                <UsersAdminPage />
              </RequireAuth>
            }
          />
          <Route path="/p/:token" element={<SharePage />} />

          {/* Client portal */}
          <Route path="/c/login" element={<ClientLoginPage />} />
          <Route
            path="/c/dashboard"
            element={
              <RequireClientAuth>
                <ClientDashboardPage />
              </RequireClientAuth>
            }
          />
          <Route
            path="/c/projects/:id"
            element={
              <RequireClientAuth>
                <ClientProjectPage />
              </RequireClientAuth>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
}

function Header() {
  const { state: ownerState, logout: ownerLogout } = useAuth();
  const { state: clientState, logout: clientLogout } = useClientAuth();
  const ownerAuthed = ownerState.status === "authed";
  const clientAuthed = clientState.status === "authed";

  return (
    <header className="border-b border-neutral-200 bg-white">
      <div className="flex h-14 w-full items-center justify-between px-6">
        <Link to="/" className="flex items-center" aria-label="Clarity by Avista — home">
          <img
            src="/clarity_logo.svg"
            alt="Clarity by Avista"
            className="h-8 w-auto"
            width={192}
            height={62}
          />
        </Link>
        <nav className="flex items-center gap-4 text-sm text-neutral-600">
          {ownerAuthed ? (
            // Owner is signed in — show owner controls (precedence over
            // client if both happen to be active on the same browser).
            <>
              <Link to="/dashboard" className="hover:text-neutral-900">
                Dashboard
              </Link>
              <span className="text-neutral-400">·</span>
              <span className="text-neutral-700">{ownerState.user.name}</span>
              <button
                type="button"
                onClick={() => void ownerLogout()}
                className="rounded-md border border-neutral-300 px-3 py-1.5 hover:bg-neutral-100"
              >
                Log out
              </button>
            </>
          ) : clientAuthed ? (
            <>
              <Link to="/c/dashboard" className="hover:text-neutral-900">
                Your reviews
              </Link>
              <span className="text-neutral-400">·</span>
              <span className="text-neutral-700">
                {clientState.me.name ?? clientState.me.email}
              </span>
              <button
                type="button"
                onClick={() => void clientLogout()}
                className="rounded-md border border-neutral-300 px-3 py-1.5 hover:bg-neutral-100"
              >
                Log out
              </button>
            </>
          ) : ownerState.status === "loading" ||
            clientState.status === "loading" ? null : (
            <>
              <Link to="/login" className="hover:text-neutral-900">
                Log in
              </Link>
              <Link
                to="/signup"
                className="rounded-md bg-neutral-900 px-3 py-1.5 text-white hover:bg-neutral-700"
              >
                Sign up
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function NotFound() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-20 text-center">
      <h1 className="text-2xl font-semibold">Not found</h1>
      <p className="mt-2 text-neutral-600">That page doesn’t exist.</p>
    </div>
  );
}
